<template>
  <v-app id="scatt-app">
    <Scatt
      ref="scatt"
      v-bind:annotator-id="overridenAnnotatorInfo.id"
      v-bind:annotator-name="overridenAnnotatorInfo.name"
      v-bind:editor-definitions="editorDefinitions"
      v-bind:segment-content-data-input-definitions="segmentContentDataInputDefinitions"
      v-bind:segment-data-types="segmentDataTypes"
      v-on:before-unload="$_onBeforeUnload"
      v-on:change="$_onChange"
    >
      <template
        v-if="isServerMode"
        v-slot:app-bar="{ callback, state }"
      >
        <template v-if="isSignedIn">
          <AppBarButton
            v-bind:disabled="state.isUndoTargetEmpty"
            v-on:click.stop="callback.undoChange"
          >
            <v-icon>mdi-undo</v-icon>Undo
          </AppBarButton>

          <AppBarButton
            v-bind:disabled="state.isRedoTargetEmpty"
            v-on:click.stop="callback.redoChange"
          >
            <v-icon>mdi-redo</v-icon>Redo
          </AppBarButton>

          <AppBarButton v-on:click.stop="callback.loadScattDataFromLocalFile">
            <v-icon>mdi-folder-open</v-icon>Load
          </AppBarButton>

          <AppBarButton v-on:click.stop="callback.downloadScattData">
            <v-icon>mdi-download</v-icon>Download
          </AppBarButton>

          <AppBarButton v-on:click.stop="callback.showViewFilter">
            <v-icon>mdi-format-list-checkbox</v-icon>Filter view
          </AppBarButton>

          <AppBarButton v-on:click.stop="callback.showHelp">
            <v-icon>mdi-help-circle</v-icon>Help
          </AppBarButton>

          <v-spacer />

          <v-menu bottom left offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                small icon
                v-bind="attrs"
                v-on="on"
              >
                <v-avatar size="30">
                  <v-img
                    v-if="annotatorInfo.profileImageUrl"
                    v-bind:src="annotatorInfo.profileImageUrl"
                  />
                  <v-icon v-else>mdi-dots-vertical</v-icon>
                </v-avatar>
              </v-btn>
            </template>

            <v-list dense>
              <v-list-item
                v-bind:disabled="!isSignedIn"
                v-on:click.stop="$_signOut"
              >
                <v-list-item-title>Sign Out</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>

        <v-btn
          text
          v-else
          v-on:click="$_signIn"
        >
          Sign In
        </v-btn>
      </template>
    </Scatt>
  </v-app>
</template>

<style>
#scatt-app {
  position: relative;
  height: 100vh;
}

html {
  overflow: hidden;
}
</style>

<script>
import Vue from 'vue'
import Scatt, {
  AppBarButton,
  TimelineSegmentInspectorDefinition,
  PlaybackHistorySegmentData,
  utils,
} from '@Scatt'

import TimelineLabelForCefrEnumerator from '@ScattPlugin/components/Editors/TimelineLabelForCefrEnumerator.vue'
import CefrScoreEditor from '@ScattPlugin/components/Editors/CefrScoreEditor.vue'
import SummaryNoteEditor from '@ScattPlugin/components/Editors/SummaryNoteEditor.vue'
import LabelForCefrSegmentContentDataInput from '@ScattPlugin/components/Parts/Input/LabelForCefrSegmentContentDataInput.vue'

import LabelForCefrSegmentData from '@ScattPlugin/modules/segmentDataTypes/LabelForCefrSegmentData.js'
import CefrScoreSegmentData from '@ScattPlugin/modules/segmentDataTypes/CefrScoreSegmentData.js'
import CefrScoreChangeHistorySegmentData from '@ScattPlugin/modules/segmentDataTypes/CefrScoreChangeHistorySegmentData.js'

const googleClientId = '905089697981-7boic18tol71f73mdok65a0gbg9bob6f.apps.googleusercontent.com';
const googleCloudStorageFileNameDelimiter = '%2F';

const scattDataLocalStorageKey = 'langxScattData';

class AnnotatorInfo {
  static name = 'AnnotatorInfo';

  constructor(id, name, profileImageUrl = undefined) {
    this.id = id;
    this.name = name;
    this.profileImageUrl = profileImageUrl;
  }
}

const localAnnotatorInfo = new AnnotatorInfo('scatt', 'Scatt');

const mode = {
  local: 'local',
  server: 'server',
};

export default {
  components: { Scatt, AppBarButton },

  computed: {
    editorDefinitions() {
      return [
        CefrScoreEditor.editorDefinition,
        TimelineLabelForCefrEnumerator.editorDefinition,
        SummaryNoteEditor.editorDefinition,
        TimelineSegmentInspectorDefinition,
      ];
    },

    segmentContentDataInputDefinitions() {
      return [
        LabelForCefrSegmentContentDataInput.segmentContentDataInputDefinition,
      ];
    },

    segmentDataTypes() {
      return [
        LabelForCefrSegmentData,
        CefrScoreSegmentData,
        CefrScoreChangeHistorySegmentData,
        PlaybackHistorySegmentData,
      ];
    },

    mode() {
      if (this.resourceId === undefined) {
        return mode.local;
      } else {
        return mode.server;
      }
    },

    isServerMode() {
      return this.mode === mode.server;
    },

    isSignedIn() {
      if (this.accessTokenType === undefined) return false;
      if (this.accessToken === undefined) return false;
      if (this.annotatorInfo === undefined) return false;
      return true;
    },

    overridenAnnotatorInfo() {
      if (this.annotatorInfo !== undefined) {
        return this.annotatorInfo;
      } else {
        return localAnnotatorInfo;
      }
    },

    authorization() {
      if (this.accessTokenType === undefined) return undefined;
      if (this.accessToken === undefined) return undefined;
      return `${this.accessTokenType} ${this.accessToken}`;
    },
  },

  data() {
    return {
      gsi: undefined,
      accessTokenType: undefined,
      accessToken: undefined,
      annotatorInfo: undefined,
      resourceId: undefined,
      scattDataRawObj: undefined,
      scattDataBackupRawObj: undefined,
      isLoadingScattDataCompleted: false,
      signInTimeoutId: undefined,
    };
  },

  created() {
    Vue.component(
      SummaryNoteEditor.editorDefinition.componentName,
      SummaryNoteEditor,
    );
    Vue.component(
      CefrScoreEditor.editorDefinition.componentName,
      CefrScoreEditor,
    );
    Vue.component(
      TimelineLabelForCefrEnumerator.editorDefinition.componentName,
      TimelineLabelForCefrEnumerator,
    );
    Vue.component(
      LabelForCefrSegmentContentDataInput.segmentContentDataInputDefinition.componentName,
      LabelForCefrSegmentContentDataInput,
    )
  },

  async mounted() {
    this.resourceId = this.$route.params['resource_id'];
    if (this.resourceId) {
      this.gsi = await utils.loadExternalScript('https://accounts.google.com/gsi/client', { key: 'google' });
      await this.$_signIn();
      await this.$_downloadData();
    } else {
      let scattDataJsonString = localStorage.getItem(scattDataLocalStorageKey);
      if (scattDataJsonString !== null) {
        try {
          let scattDataRawObj = JSON.parse(scattDataJsonString);
          this.$refs.scatt.loadScattData(scattDataRawObj);
        } catch (error) {
          if (error instanceof SyntaxError) {
            this.$refs.scatt.registerFooterMessage(error.message);
          } else {
            throw error;
          }
        }
      }
    }
  },

  beforeDestroy() {
    if (this.signInTimeoutId !== undefined) {
      window.clearTimeout(this.signInTimeoutId);
    }
  },

  methods: {
    async $_signIn() {
      // NOTE: { prompt: '' } is needed to avoid reasking for permissions
      let tokenResponse = await new Promise(resolve => {
        let tokenClient = this.gsi.accounts.oauth2.initTokenClient({
          client_id: googleClientId,
          scope: 'https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/devstorage.read_write',
          callback: resolve,
          prompt: '',
          hd: 'equ.ai',
        });
        tokenClient.requestAccessToken();
      });
      this.accessTokenType = tokenResponse['token_type'];
      this.accessToken = tokenResponse['access_token'];
      this.signInTimeoutId = window.setTimeout(
        async () => {
          this.accessTokenType = undefined;
          this.accessToken = undefined;
          this.annotatorInfo = undefined;
          this.signInTimeoutId = undefined;
          await this.$_signIn();
        },
        tokenResponse['expires_in'] * 1000,
      );

      let userInfoResponse = await fetch(
        'https://www.googleapis.com/oauth2/v3/userinfo',
        {
          headers: {
            'Authorization': this.authorization,
          },
        },
      );
      let userInfo = await userInfoResponse.json();
      this.annotatorInfo = new AnnotatorInfo(
        userInfo['sub'],
        userInfo['name'],
        userInfo['picture'],
      );
    },

    async $_downloadData() {

      const downloadDataFromGcsAs = async (url, contentType, as) => {
        return await new Promise(
          (resolve, reject) => {
            fetch(
                `${url}?alt=media`,
                {
                  headers: { 'Content-Type': contentType, 'Authorization': this.authorization }
                },
              )
              .then(response => {
                if (response.ok) {
                  response[as]().then(resolve, reject);
                } else {
                  throw new Error(`Error ${response.status}: ${response.statusText}`);
                }
              })
              .catch(error => {
                this.$refs.scatt.registerFooterMessage(error.message);
                resolve(undefined);
              });
          }
        );
      };

      const d = googleCloudStorageFileNameDelimiter;
      const userScattDataName = `${this.resourceId}${d}${this.annotatorInfo.id}${d}scatt_data.json`;
      const templateScattDataName = `${this.resourceId}${d}scatt_data.json`;
      const videoDataName = `${this.resourceId}${d}video`;

      this.$refs.scatt.registerFooterMessage('Initializing...');
      downloadDataFromGcsAs(`https://storage.googleapis.com/storage/v1/b/scatt-datastore/o/${userScattDataName}`, 'application/json', 'json')
        .then(scattData => {
          if (scattData === undefined) {
            return downloadDataFromGcsAs(`https://storage.googleapis.com/storage/v1/b/scatt-datastore/o/${templateScattDataName}`, 'application/json', 'json')
          } else {
            return scattData;
          }
        })
        .then(scattData => {
          if (scattData === undefined) {
            this.$refs.scatt.registerFooterMessage('Failed to load Scatt data.');
          } else {
            this.$refs.scatt.loadScattData(scattData);
            this.isLoadingScattDataCompleted = true;
          }
        });

      downloadDataFromGcsAs(`https://storage.googleapis.com/storage/v1/b/scatt-datastore/o/${videoDataName}`, 'video/mp4', 'arrayBuffer')
        .then(videoData => {
          if (videoData === undefined) {
            this.$refs.scatt.registerFooterMessage('Failed to load video data.');
          } else {
            this.$refs.scatt.loadVideoData(new Uint8Array(videoData));
          }
        });

      fetch(
          `https://services.langx.scatt.io/waveform-digest/generate/scatt-datastore/${videoDataName}`,
          {
            method: 'POST',
            headers: { 'Authorization': this.authorization },
          },
        )
        .then(response => response.text())
        .then(waveformDigestUrl => downloadDataFromGcsAs(waveformDigestUrl, 'application/octet-stream', 'arrayBuffer'))
        .then(waveformDigestData => this.$refs.scatt.loadWaveformDigestData(new Uint8Array(waveformDigestData)));
    },

    $_onBeforeUnload(beforeUnloadEvent, isCurrentScattDataSameAsLastSaved) {
      if (!isCurrentScattDataSameAsLastSaved) {
        beforeUnloadEvent.preventDefault();
        beforeUnloadEvent.returnValue = '';
      }
    },

    async $_onChange(scattDataRawObj) {
      if (!this.isLoadingScattDataCompleted) return;
      if (this.isServerMode && this.isSignedIn) {
        const d = googleCloudStorageFileNameDelimiter;
        let fileName = `${this.resourceId}${d}${this.annotatorInfo.id}${d}scatt_data.json`;
        await fetch(
          `https://storage.googleapis.com/upload/storage/v1/b/scatt-datastore/o?uploadType=media&name=${fileName}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': this.authorization },
            body: JSON.stringify(scattDataRawObj),
          }
        );
      }
    },

    $_signOut() {
      this.gsi.accounts.oauth2.revoke(
        this.accessToken,
        done => {
          if (done.successful) {
            this.accessTokenType = undefined;
            this.accessToken = undefined;
            this.annotatorInfo = undefined;
          } else {
            this.$refs.scatt.registerFooterMessage(`${done.error}: ${done.error_description}`);
          }
        },
      );
    },
  },
};
</script>